// src/pages/StudentDashboard.jsx — Pro redesign: dark hero, KPI strip, mini-leaderboard.
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { collection, getDocs, query, orderBy, limit } from "firebase/firestore";
import { motion } from "framer-motion";
import {
  HiFire, HiStar, HiArrowRight, HiLightningBolt, HiTrendingUp, HiChartBar,
} from "react-icons/hi";
import { db } from "../components/firebase/firebase";
import { useAuth } from "../context/AuthContext";
import Loader from "../components/common/Loader";
import { SEO } from "../components/common/SEO";
import OnboardingModal from "../components/student/OnboardingModal";
import DailyChallenge from "../components/student/DailyChallenge";
import SubjectBars from "../components/student/SubjectBars";
import AnimatedCounter from "../components/student/AnimatedCounter";
import { BADGES, TIER_STYLES, levelProgress, WEAK_THRESHOLD } from "../lib/gamification";
import { goalProgress, currentWeeklyXp } from "../lib/goals";

const BOARD_LABELS = { state: "State Board", cbse: "CBSE", competitive: "Competitive" };

function subjectData(topicStats) {
  const map = {};
  for (const t of topicStats) {
    if (!t.subject) continue;
    if (!map[t.subject]) map[t.subject] = { subject: t.subject, totalCorrect: 0, totalQuestions: 0, attempts: 0 };
    map[t.subject].totalCorrect += t.totalCorrect || 0;
    map[t.subject].totalQuestions += t.totalQuestions || 0;
    map[t.subject].attempts += t.attempts || 0;
  }
  return Object.values(map).map((s) => ({
    ...s,
    accuracy: s.totalQuestions > 0 ? s.totalCorrect / s.totalQuestions : 0,
  }));
}

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Floating decorative blob, ported from the Home hero.
function FloatingBlob({ className, delay = 0, duration = 7 }) {
  return (
    <motion.div
      className={`absolute rounded-full pointer-events-none ${className}`}
      animate={{ y: [0, -22, 0], x: [0, 12, 0], scale: [1, 1.06, 1] }}
      transition={{ duration, repeat: Infinity, delay, ease: "easeInOut" }}
    />
  );
}

// Drifting math symbol, ported from the Home hero.
function MathSymbol({ symbol, className, duration = 10 }) {
  return (
    <motion.div
      className={`absolute text-white/15 font-bold select-none pointer-events-none ${className}`}
      animate={{ y: [0, -12, 0], rotate: [0, 5, 0] }}
      transition={{ duration, repeat: Infinity, ease: "easeInOut" }}
    >
      {symbol}
    </motion.div>
  );
}

// Thin animated progress bar used in the hero.
function HeroBar({ label, value, max, suffix, color }) {
  const pct = max > 0 ? Math.min(Math.round((value / max) * 100), 100) : 0;
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-semibold text-white/60">{label}</span>
        <span className="text-xs font-bold text-white/80">{value}{suffix} / {max}{suffix}</span>
      </div>
      <div className="h-2 bg-white/10 rounded-full overflow-hidden">
        <motion.div
          className={`h-full rounded-full ${color}`}
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 1.1, ease: "easeOut", delay: 0.3 }}
        />
      </div>
    </div>
  );
}

export default function StudentDashboard() {
  const { user, profile, loading, refreshProfile } = useAuth();
  const [topicStats, setTopicStats] = useState([]);
  const [attempts, setAttempts] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);
  const [leaders, setLeaders] = useState([]);

  const needsOnboarding = profile && (!profile.board || !profile.grade);

  // Fetch quiz data
  useEffect(() => {
    if (!user) return;
    let active = true;
    (async () => {
      setDataLoading(true);
      try {
        const [tsSnap, atSnap] = await Promise.all([
          getDocs(collection(db, "students", user.uid, "topicStats")),
          getDocs(query(collection(db, "students", user.uid, "attempts"), orderBy("takenAt", "desc"), limit(10))),
        ]);
        if (!active) return;
        setTopicStats(tsSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setAttempts(atSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (e) {
        console.error("Dashboard load failed", e);
      } finally {
        if (active) setDataLoading(false);
      }
    })();
    return () => { active = false; };
  }, [user]);

  // Fetch top-3 leaderboard silently
  useEffect(() => {
    (async () => {
      try {
        const snap = await getDocs(
          query(collection(db, "students"), orderBy("xp", "desc"), limit(3))
        );
        setLeaders(snap.docs.map((d) => ({ uid: d.id, ...d.data() })));
      } catch (_) { /* fail silently */ }
    })();
  }, []);

  const weakTopics = useMemo(
    () => topicStats
      .filter((t) => (t.attempts || 0) >= 1 && (t.accuracy ?? 1) < WEAK_THRESHOLD)
      .sort((a, b) => (a.accuracy ?? 1) - (b.accuracy ?? 1)),
    [topicStats]
  );

  const subjects = useMemo(() => subjectData(topicStats), [topicStats]);

  const overallAccuracy = useMemo(() => {
    const total = topicStats.reduce((s, t) => s + (t.totalQuestions || 0), 0);
    const correct = topicStats.reduce((s, t) => s + (t.totalCorrect || 0), 0);
    return total > 0 ? Math.round((correct / total) * 100) : null;
  }, [topicStats]);

  if (loading || !profile) return <Loader />;

  const lp = levelProgress(profile.xp || 0);
  const gp = goalProgress(profile);
  const weekXp = currentWeeklyXp(profile);
  const ownedBadges = new Set(profile.badges || []);
  const totalQuizzes = profile.totalAttempts || 0;
  const streak = profile.streak?.current || 0;
  const isNewStudent = totalQuizzes === 0;

  const today = new Date();
  const dateStr = `${DAYS[today.getDay()]}, ${today.getDate()} ${MONTHS[today.getMonth()]}`;
  const boardLabel = BOARD_LABELS[profile.board] || profile.board || "";
  const classLabel = profile.grade ? `Class ${profile.grade}` : "";

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO title="My Dashboard" description="Track your quiz scores, XP, streaks and weak topics." canonical="/dashboard" />

      {needsOnboarding && <OnboardingModal uid={user.uid} onDone={refreshProfile} />}

      <div className="max-w-screen-lg mx-auto px-4 sm:px-6 py-6 space-y-5">

        {/* ── 1. HERO IDENTITY CARD ── */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-700 text-white p-6 sm:p-8 shadow-2xl shadow-blue-900/40"
        >
          {/* Dot-grid overlay (separate layer so it doesn't override the gradient) */}
          <div
            aria-hidden
            className="absolute inset-0 opacity-10 pointer-events-none"
            style={{
              backgroundImage: "radial-gradient(circle, white 1px, transparent 1px)",
              backgroundSize: "24px 24px",
            }}
          />

          {/* Decorative floating blobs */}
          <FloatingBlob className="w-48 h-48 bg-white/10 blur-3xl -top-12 -right-12" delay={0} duration={8} />
          <FloatingBlob className="w-40 h-40 bg-indigo-400/20 blur-3xl -bottom-16 left-1/3" delay={2} duration={10} />
          <FloatingBlob className="w-32 h-32 bg-sky-300/20 blur-2xl top-1/3 left-1/4" delay={1} duration={7} />

          {/* Drifting math symbols */}
          <MathSymbol symbol="π" className="text-5xl top-4 left-6" duration={8.2} />
          <MathSymbol symbol="∑" className="text-4xl top-6 right-10" duration={9.7} />
          <MathSymbol symbol="√" className="text-6xl bottom-4 left-1/4" duration={11.1} />
          <MathSymbol symbol="∫" className="text-5xl top-1/2 left-1/2" duration={7.3} />
          <MathSymbol symbol="∞" className="text-4xl bottom-6 right-1/4" duration={10.8} />

          <div className="relative flex flex-col sm:flex-row sm:items-center gap-6">
            {/* Left: identity */}
            <div className="flex-1 min-w-0">
              <p className="text-blue-200 text-xs font-bold uppercase tracking-widest mb-1">{dateStr}</p>
              <h1 className="text-2xl sm:text-3xl font-black text-white leading-tight">
                {greeting()}, {profile.handle || "Student"}! 👋
              </h1>

              <div className="flex flex-wrap items-center gap-2 mt-3">
                {(classLabel || boardLabel) && (
                  <span className="inline-flex items-center gap-1.5 bg-white/10 border border-white/15 text-white/80 text-xs font-semibold px-3 py-1 rounded-full">
                    {classLabel}{classLabel && boardLabel ? " · " : ""}{boardLabel}
                  </span>
                )}
                {streak > 0 && (
                  <span className="inline-flex items-center gap-1 bg-orange-500/20 border border-orange-400/30 text-orange-300 text-xs font-bold px-3 py-1 rounded-full">
                    <HiFire className="text-orange-400" /> {streak}-day streak
                  </span>
                )}
              </div>
            </div>

            {/* Right: progress bars */}
            <div className="sm:w-64 space-y-3 shrink-0">
              <HeroBar
                label={`Level ${profile.level || 1}`}
                value={profile.xp || 0}
                max={(profile.xp || 0) + lp.toNext}
                suffix=" XP"
                color="bg-white/90"
              />
              <HeroBar
                label="⚡ Weekly Goal"
                value={weekXp}
                max={gp.goal}
                suffix=" XP"
                color={gp.met ? "bg-yellow-300" : "bg-sky-300"}
              />
              {gp.met && (
                <p className="text-xs text-yellow-300 font-bold text-right">🎉 Goal smashed!</p>
              )}
            </div>
          </div>
        </motion.div>

        {/* ── 2. KPI STRIP ── */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            {
              label: "Day Streak",
              value: streak,
              suffix: "",
              icon: HiFire,
              topBorder: "border-t-4 border-orange-400",
              iconBg: "bg-orange-50 text-orange-500",
              num: "text-gray-800",
            },
            {
              label: "Total XP",
              value: profile.xp || 0,
              suffix: "",
              icon: HiStar,
              topBorder: "border-t-4 border-amber-400",
              iconBg: "bg-amber-50 text-amber-500",
              num: "text-gray-800",
            },
            {
              label: "Level",
              value: profile.level || 1,
              suffix: "",
              icon: HiLightningBolt,
              topBorder: "border-t-4 border-indigo-400",
              iconBg: "bg-indigo-50 text-indigo-500",
              num: "text-gray-800",
            },
            {
              label: "Accuracy",
              value: overallAccuracy,
              suffix: "%",
              icon: HiTrendingUp,
              topBorder: "border-t-4 border-sky-400",
              iconBg: "bg-sky-50 text-sky-500",
              num: "text-gray-800",
              isNull: overallAccuracy === null,
            },
          ].map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.06 }}
                className={`bg-white rounded-2xl shadow-sm border border-gray-100 ${s.topBorder} p-4 flex flex-col`}
              >
                <div className="flex items-start justify-between mb-3">
                  <p className="text-xs font-semibold text-gray-400">{s.label}</p>
                  <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${s.iconBg}`}>
                    <Icon className="text-sm" />
                  </div>
                </div>
                <p className={`text-2xl font-black ${s.num}`}>
                  {s.isNull ? (
                    <span className="text-base font-semibold text-gray-300">—</span>
                  ) : (
                    <><AnimatedCounter value={s.value || 0} />{s.suffix}</>
                  )}
                </p>
              </motion.div>
            );
          })}
        </div>

        {/* ── 3. QUICK ACTIONS ── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Link
            to="/mcq"
            className="group relative overflow-hidden bg-gradient-to-br from-blue-600 to-indigo-700 rounded-2xl p-5 flex items-center gap-4 shadow-xl shadow-blue-500/25 hover:shadow-blue-500/40 transition-shadow"
          >
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,rgba(255,255,255,0.12),transparent)]" />
            <div className="relative w-13 h-13 w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center text-2xl shrink-0 shadow-lg">
              📝
            </div>
            <div className="relative text-white min-w-0 flex-1">
              <p className="font-black text-base">Take a Quiz</p>
              <p className="text-blue-100 text-sm mt-0.5">Earn XP &amp; level up your score</p>
            </div>
            <HiArrowRight className="relative text-white/60 text-xl shrink-0 group-hover:translate-x-1 transition-transform" />
          </Link>

          <Link
            to="/practice"
            className="group relative overflow-hidden bg-gradient-to-br from-sky-500 to-blue-600 rounded-2xl p-5 flex items-center gap-4 shadow-xl shadow-sky-500/25 hover:shadow-sky-500/40 transition-shadow"
          >
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,rgba(255,255,255,0.12),transparent)]" />
            <div className="relative w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center text-2xl shrink-0 shadow-lg">
              📚
            </div>
            <div className="relative text-white min-w-0 flex-1">
              <p className="font-black text-base">Study Materials</p>
              <p className="text-blue-100 text-sm mt-0.5">Past papers &amp; practice resources</p>
            </div>
            <HiArrowRight className="relative text-white/60 text-xl shrink-0 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>

        {/* ── 4. DAILY CHALLENGE / FIRST QUIZ CTA ── */}
        {isNewStudent ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            className="bg-gradient-to-r from-blue-600 to-indigo-700 rounded-3xl p-6 text-white shadow-lg shadow-blue-500/20"
          >
            <p className="text-xs font-bold uppercase tracking-widest text-blue-100 mb-1">Get started</p>
            <h2 className="text-xl font-black mb-1">Take your first quiz! 🎉</h2>
            <p className="text-blue-100 text-sm mb-4">
              Jump in and start earning XP, building your streak, and unlocking badges.
            </p>
            <Link
              to="/mcq"
              className="inline-flex items-center gap-2 bg-white text-blue-700 font-bold px-5 py-2.5 rounded-2xl hover:bg-blue-50 transition-colors text-sm shadow-lg"
            >
              Start Now <HiArrowRight />
            </Link>
          </motion.div>
        ) : (
          !dataLoading && profile.board && (
            <DailyChallenge uid={user.uid} profile={profile} topicStats={topicStats} attempts={attempts} />
          )
        )}

        {/* ── 5. DATA SECTIONS ── */}
        {dataLoading ? (
          <div className="py-10"><Loader /></div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

            {/* ── Left 2/3 ── */}
            <div className="lg:col-span-2 space-y-5">

              {/* Subject Performance */}
              {subjects.length > 0 ? (
                <motion.div
                  initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                  className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5"
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-lg bg-indigo-50 flex items-center justify-center">
                        <HiTrendingUp className="text-indigo-500 text-sm" />
                      </div>
                      <h2 className="font-extrabold text-gray-800">Subject Performance</h2>
                    </div>
                    <Link to="/progress" className="text-xs text-indigo-600 font-semibold hover:underline flex items-center gap-0.5">
                      Full report <HiArrowRight className="text-sm" />
                    </Link>
                  </div>
                  <SubjectBars data={subjects} compact />
                </motion.div>
              ) : !isNewStudent && (
                <div className="bg-indigo-50 border border-indigo-100 rounded-2xl p-5 text-center">
                  <span className="text-3xl block mb-2">📊</span>
                  <p className="font-bold text-indigo-700 text-sm">No subject data yet</p>
                  <p className="text-indigo-400 text-xs mt-1">Take a few more quizzes and your breakdown will appear here.</p>
                </div>
              )}

              {/* Weak Topics */}
              <motion.div
                initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
                className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-lg bg-red-50 flex items-center justify-center">
                      <span className="text-sm">🎯</span>
                    </div>
                    <h2 className="font-extrabold text-gray-800">Focus Areas</h2>
                  </div>
                  {weakTopics.length > 4 && (
                    <Link to="/progress" className="text-xs text-indigo-600 font-semibold hover:underline">
                      View all {weakTopics.length} →
                    </Link>
                  )}
                </div>
                {weakTopics.length === 0 ? (
                  <div className="text-center py-4">
                    <span className="text-3xl block mb-2">🌟</span>
                    <p className="text-sm font-semibold text-gray-500">
                      {isNewStudent ? "Take some quizzes to pinpoint your weak topics." : "No weak topics right now — great work!"}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {weakTopics.slice(0, 4).map((t) => {
                      const acc = Math.round((t.accuracy ?? 0) * 100);
                      const materialsLink = t.subject && t.board && t.grade
                        ? `/resources/${t.board}/${t.grade}/${t.subject}` : "/practice";
                      return (
                        <div key={t.id} className="flex items-center gap-3 p-3 rounded-xl border border-red-50 bg-red-50/50 hover:bg-red-50 transition-colors">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <p className="font-semibold text-sm text-gray-800 truncate">{t.topic || "Untitled"}</p>
                              {t.subject && (
                                <span className="text-[10px] font-bold uppercase bg-red-100 text-red-600 px-1.5 py-0.5 rounded-full shrink-0">{t.subject}</span>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <div className="flex-1 h-1.5 bg-red-100 rounded-full overflow-hidden">
                                <div className="h-full bg-red-400 rounded-full" style={{ width: `${acc}%` }} />
                              </div>
                              <span className="text-xs font-bold text-red-500 shrink-0">{acc}%</span>
                            </div>
                          </div>
                          <div className="flex gap-1.5 shrink-0">
                            <Link to="/mcq" className="text-xs font-bold bg-indigo-600 text-white px-2.5 py-1 rounded-lg hover:bg-indigo-700 transition-colors">Retake</Link>
                            <Link to={materialsLink} className="text-xs font-bold bg-white border border-gray-200 text-gray-600 px-2.5 py-1 rounded-lg hover:bg-gray-50 transition-colors">Study</Link>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </motion.div>

              {/* Recent Quizzes */}
              <motion.div
                initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
                className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-lg bg-amber-50 flex items-center justify-center">
                      <HiStar className="text-amber-500 text-sm" />
                    </div>
                    <h2 className="font-extrabold text-gray-800">Recent Quizzes</h2>
                  </div>
                  <Link to="/progress" className="text-xs text-indigo-600 font-semibold hover:underline flex items-center gap-0.5">
                    Full history <HiArrowRight className="text-sm" />
                  </Link>
                </div>
                {attempts.length === 0 ? (
                  <div className="text-center py-6">
                    <span className="text-4xl block mb-3">📝</span>
                    <p className="text-sm font-semibold text-gray-600 mb-3">No quizzes taken yet!</p>
                    <Link to="/mcq" className="inline-flex items-center gap-1 bg-blue-600 text-white font-bold text-sm px-4 py-2 rounded-xl hover:bg-blue-700 transition-colors">
                      Take your first quiz <HiArrowRight />
                    </Link>
                  </div>
                ) : (
                  <div className="space-y-1">
                    {attempts.slice(0, 5).map((a) => {
                      const pass = (a.pct ?? 0) >= 50;
                      const exam = a.mode === "exam";
                      const when = a.takenAt?.toDate
                        ? a.takenAt.toDate().toLocaleDateString(undefined, { day: "numeric", month: "short" })
                        : "";
                      return (
                        <div key={a.id} className={`flex items-center gap-3 py-3 pl-3 pr-2 rounded-xl border-l-4 ${pass ? "border-blue-400 bg-blue-50/30" : "border-red-400 bg-red-50/30"}`}>
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-sm text-gray-800 truncate">{a.topic || "Quiz"}</p>
                            <p className="text-xs text-gray-400 mt-0.5">{a.correct}/{a.total} correct · {when}</p>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${exam ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"}`}>
                              {exam ? "Exam" : "Practice"}
                            </span>
                            <span className={`text-xs font-bold px-2.5 py-1 rounded-lg ${pass ? "bg-blue-100 text-blue-700" : "bg-red-100 text-red-700"}`}>
                              {a.pct}%
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </motion.div>
            </div>

            {/* ── Right 1/3 ── */}
            <div className="space-y-5">

              {/* Badges — horizontal pill scroll */}
              <motion.div
                initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.12 }}
                className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5"
              >
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-extrabold text-gray-800">Badges</h2>
                  <Link to="/profile" className="text-xs text-indigo-600 font-semibold hover:underline">See all →</Link>
                </div>
                <div className="flex flex-wrap gap-2">
                  {BADGES.map((b) => {
                    const earned = ownedBadges.has(b.id);
                    const ts = TIER_STYLES[b.tier] || TIER_STYLES.bronze;
                    return (
                      <div
                        key={b.id}
                        title={b.description}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-bold transition-all ${
                          earned
                            ? `${ts.chip} border-transparent shadow-sm`
                            : "bg-gray-50 border-gray-100 text-gray-400 opacity-50"
                        }`}
                        style={earned && b.tier === "gold" ? { boxShadow: "0 0 0 2px rgba(251,191,36,0.25)" } : {}}
                      >
                        <span className={earned ? "" : "grayscale"}>{b.icon}</span>
                        {b.name}
                      </div>
                    );
                  })}
                </div>
              </motion.div>

              {/* Mini Leaderboard */}
              {leaders.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.18 }}
                  className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5"
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-lg bg-amber-50 flex items-center justify-center">
                        <HiChartBar className="text-amber-500 text-sm" />
                      </div>
                      <h2 className="font-extrabold text-gray-800">Top Students</h2>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {leaders.map((l, i) => {
                      const isMe = l.uid === user.uid;
                      const medals = ["🥇", "🥈", "🥉"];
                      return (
                        <div
                          key={l.uid}
                          className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors ${
                            isMe ? "bg-indigo-50 border border-indigo-100" : "bg-gray-50"
                          }`}
                        >
                          <span className="text-lg w-6 text-center shrink-0">{medals[i]}</span>
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-bold truncate ${isMe ? "text-indigo-700" : "text-gray-800"}`}>
                              {l.handle || "Student"}{isMe && " (You)"}
                            </p>
                          </div>
                          <span className={`text-xs font-black shrink-0 ${isMe ? "text-indigo-600" : "text-gray-500"}`}>
                            {(l.xp || 0).toLocaleString()} XP
                          </span>
                        </div>
                      );
                    })}
                  </div>
                  <Link to="/leaderboard" className="mt-3 flex items-center justify-end gap-1 text-xs text-indigo-600 font-semibold hover:underline">
                    Full leaderboard <HiArrowRight className="text-xs" />
                  </Link>
                </motion.div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
